import React, { useState, useMemo } from 'react';
import { CheckCircle, Clock, FileText, Truck, PackageCheck, ChevronRight, Search, ArrowRight, ChevronDown, ChevronUp, Package, MapPin, Calendar, Building2, X } from 'lucide-react';

const fmtINR = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString('en-IN');
};

const STAGES = [
  { id: 'confirmed', label: 'Confirmed', color: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0', icon: CheckCircle, source: 'oc' },
  { id: 'ready', label: 'Ready to Dispatch', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', icon: Clock, source: 'oc' },
  { id: 'invoiced', label: 'Invoiced', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', icon: FileText, source: 'oc' },
  { id: 'dispatched', label: 'Dispatched', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', icon: Truck, source: 'dispatch' },
  { id: 'in_transit', label: 'In Transit', color: '#0ea5e9', bg: '#f0f9ff', border: '#bae6fd', icon: Truck, source: 'dispatch' },
  { id: 'delivered', label: 'Delivered', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: PackageCheck, source: 'dispatch' },
];

const KanbanCard = ({ item, stage, onClick }) => (
  <div
    data-testid={`kanban-card-${item.id}`}
    onClick={() => onClick(item)}
    className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-50 hover:shadow-md hover:border-gray-200 transition-all cursor-pointer group"
  >
    <div className="flex items-center justify-between mb-1">
      <p className="text-[10px] font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
        {item.oc_number || item.dispatch_number}
      </p>
      <p className="text-[9px] font-bold" style={{ color: stage.color }}>
        {fmtINR(item.total || item.value || 0)}
      </p>
    </div>
    <p className="text-[9px] text-gray-500 truncate">
      {item.customer?.name || item.customer_name || 'Unknown'}
    </p>
    {item.items?.length > 0 && (
      <div className="flex items-center gap-1 mt-1">
        <Package className="w-2.5 h-2.5 text-gray-300" />
        <span className="text-[8px] text-gray-400">{item.items.length} item{item.items.length > 1 ? 's' : ''}</span>
      </div>
    )}
    {item.date && (
      <div className="flex items-center gap-1 mt-0.5">
        <Calendar className="w-2.5 h-2.5 text-gray-300" />
        <span className="text-[8px] text-gray-400">{item.date}</span>
      </div>
    )}
  </div>
);

const DetailPanel = ({ item, stage, onClose }) => {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md m-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="kanban-detail-panel">
        <div className="flex items-center justify-between p-4 border-b border-gray-100" style={{ backgroundColor: `${stage.color}08` }}>
          <div className="flex items-center gap-2">
            <stage.icon className="w-4 h-4" style={{ color: stage.color }} />
            <div>
              <p className="text-xs font-bold text-gray-900">{item.oc_number || item.dispatch_number}</p>
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: stage.color }}>{stage.label}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-2.5 bg-gray-50 rounded-lg">
              <p className="text-[9px] text-gray-500">Customer</p>
              <p className="text-[11px] font-semibold text-gray-900">{item.customer?.name || item.customer_name || '-'}</p>
            </div>
            <div className="p-2.5 bg-gray-50 rounded-lg">
              <p className="text-[9px] text-gray-500">Value</p>
              <p className="text-[11px] font-bold" style={{ color: stage.color }}>INR {fmtINR(item.total || item.value || 0)}</p>
            </div>
            {item.date && (
              <div className="p-2.5 bg-gray-50 rounded-lg">
                <p className="text-[9px] text-gray-500">Date</p>
                <p className="text-[11px] font-semibold text-gray-900">{item.date}</p>
              </div>
            )}
            {item.dispatch_date && (
              <div className="p-2.5 bg-gray-50 rounded-lg">
                <p className="text-[9px] text-gray-500">Dispatch Date</p>
                <p className="text-[11px] font-semibold text-gray-900">{item.dispatch_date}</p>
              </div>
            )}
            {item.from_branch && (
              <div className="p-2.5 bg-gray-50 rounded-lg">
                <p className="text-[9px] text-gray-500">From Branch</p>
                <p className="text-[11px] font-semibold text-gray-900">{item.from_branch}</p>
              </div>
            )}
            {item.invoice_number && (
              <div className="p-2.5 bg-gray-50 rounded-lg">
                <p className="text-[9px] text-gray-500">Invoice</p>
                <p className="text-[11px] font-semibold text-gray-900">{item.invoice_number}</p>
              </div>
            )}
            {item.transport_details?.vehicle_number && (
              <div className="p-2.5 bg-gray-50 rounded-lg">
                <p className="text-[9px] text-gray-500">Vehicle</p>
                <p className="text-[11px] font-semibold text-gray-900">{item.transport_details.vehicle_number}</p>
              </div>
            )}
          </div>
          {item.items?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-700 mb-1.5">Items ({item.items.length})</p>
              <div className="space-y-1">
                {item.items.slice(0, 8).map((it, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-[10px]">
                    <span className="text-gray-800 font-medium truncate max-w-[60%]">{it.name || it.product_name}</span>
                    <span className="text-gray-500">{it.qty || it.quantity} {it.unit || ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DispatchKanban = ({ ocs = [], dispatches = [] }) => {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);

  const stageData = useMemo(() => {
    const result = {};
    const q = search.toLowerCase();

    const filterItem = (item) => {
      if (!q) return true;
      return (item.oc_number || item.dispatch_number || '').toLowerCase().includes(q)
        || (item.customer?.name || item.customer_name || '').toLowerCase().includes(q);
    };

    // OC-based stages
    result.confirmed = ocs.filter(o => o.status === 'Confirmed' && filterItem(o));
    result.ready = ocs.filter(o => o.status === 'Ready to Dispatch' && !o.invoice_number && filterItem(o));
    result.invoiced = ocs.filter(o => o.status === 'Ready to Dispatch' && o.invoice_number && filterItem(o));

    // Dispatch-based stages
    result.dispatched = dispatches.filter(d => d.status === 'Pending' && filterItem(d));
    result.in_transit = dispatches.filter(d => d.status === 'In Transit' && filterItem(d));
    result.delivered = dispatches.filter(d => d.status === 'Delivered' && filterItem(d));

    return result;
  }, [ocs, dispatches, search]);

  const totalOrders = ocs.filter(o => o.status !== 'Cancelled').length + dispatches.length;
  const totalValue = ocs.filter(o => o.status !== 'Cancelled').reduce((s, o) => s + (o.total || 0), 0);

  const toggle = (id) => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6" data-testid="dispatch-kanban">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Dispatch Tracker</h3>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {totalOrders} orders | INR {fmtINR(totalValue)} pipeline value
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
            <input
              data-testid="kanban-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search OC, customer..."
              className="pl-7 pr-3 py-1.5 text-[11px] border border-gray-200 rounded-lg w-44 focus:outline-none focus:ring-1 focus:ring-blue-200"
            />
          </div>
        </div>
      </div>

      {/* Progress Flow Bar */}
      <div className="flex items-center mb-4 bg-gray-50 rounded-xl p-2">
        {STAGES.map((stage, idx) => {
          const count = (stageData[stage.id] || []).length;
          return (
            <React.Fragment key={stage.id}>
              <div className="flex-1 flex items-center gap-1.5 px-2 py-1 rounded-lg" style={count > 0 ? { backgroundColor: `${stage.color}10` } : {}}>
                <stage.icon className="w-3 h-3 flex-shrink-0" style={{ color: count > 0 ? stage.color : '#d1d5db' }} />
                <span className="text-[9px] font-bold hidden lg:inline" style={{ color: count > 0 ? stage.color : '#9ca3af' }}>{stage.label}</span>
                <span className="text-[10px] font-bold ml-auto px-1.5 py-0.5 rounded-full text-white min-w-[20px] text-center" style={{ backgroundColor: count > 0 ? stage.color : '#d1d5db' }}>{count}</span>
              </div>
              {idx < STAGES.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0 mx-0.5" />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Kanban Columns */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {STAGES.map(stage => {
          const items = stageData[stage.id] || [];
          const stageValue = items.reduce((s, i) => s + (i.total || i.value || 0), 0);
          const isCollapsed = collapsed[stage.id];
          return (
            <div key={stage.id} data-testid={`kanban-col-${stage.id}`} className="rounded-xl min-h-[200px]" style={{ backgroundColor: stage.bg, border: `1px solid ${stage.border}` }}>
              {/* Column Header */}
              <div className="p-2.5 border-b" style={{ borderColor: stage.border }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <stage.icon className="w-3.5 h-3.5" style={{ color: stage.color }} />
                    <span className="text-[10px] font-bold" style={{ color: stage.color }}>{stage.label}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: stage.color }}>{items.length}</span>
                    <button onClick={() => toggle(stage.id)} className="p-0.5 rounded hover:bg-white/50">
                      {isCollapsed ? <ChevronDown className="w-3 h-3" style={{ color: stage.color }} /> : <ChevronUp className="w-3 h-3" style={{ color: stage.color }} />}
                    </button>
                  </div>
                </div>
                {stageValue > 0 && <p className="text-[8px] mt-1" style={{ color: stage.color }}>INR {fmtINR(stageValue)}</p>}
              </div>

              {/* Cards */}
              {!isCollapsed && (
                <div className="p-2 space-y-1.5 max-h-[350px] overflow-y-auto">
                  {items.length === 0 ? (
                    <p className="text-[10px] text-gray-400 text-center py-6">No orders</p>
                  ) : items.slice(0, 12).map(item => (
                    <KanbanCard
                      key={item.id}
                      item={item}
                      stage={stage}
                      onClick={(it) => { setSelectedItem(it); setSelectedStage(stage); }}
                    />
                  ))}
                  {items.length > 12 && (
                    <p className="text-[9px] text-center pt-1" style={{ color: stage.color }}>+{items.length - 12} more</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Detail Panel */}
      {selectedItem && selectedStage && (
        <DetailPanel
          item={selectedItem}
          stage={selectedStage}
          onClose={() => { setSelectedItem(null); setSelectedStage(null); }}
        />
      )}
    </div>
  );
};

export default DispatchKanban;
